// What dropping defense rows somewhere in the plan list would do. Pure, so
// the drag hook, the ghost's second line and the panel's drop handler all
// read the same answer (usePlanListDrag.web.ts, PlanDragGhost.web.tsx,
// DefensePlanPanel.web.tsx).

export type PlanDropTarget =
  | { type: 'bundle'; id: string }
  | { type: 'feature'; id: string }
  | { type: 'loose' };

export type PlanDropOutcomeKind = 'add' | 'move' | 'remove' | 'create' | 'none';

export interface PlanDropOutcome {
  kind: PlanDropOutcomeKind;
  /** Target bundle for 'add' and 'move'. */
  bundleId?: string;
  /** The loose row a 'create' bundles the dragged rows with. */
  withFeatureId?: string;
  /** Ghost line 2: what a drop would do, or why it can't. */
  label: string;
  /** The drop can't happen; `label` says why. */
  disabled?: boolean;
}

export interface PlanDragSource {
  featureIds: string[];
  sourceBundleId: string | null;
}

interface DropPlan {
  features: { id: string; bundleId?: string }[];
  bundles: { id: string; name: string }[];
}

/** The DOM value of `data-plan-drop` for a target, and back. */
export function planDropAttr(target: PlanDropTarget): string {
  return target.type === 'loose' ? 'loose' : `${target.type}:${target.id}`;
}

export function parsePlanDropAttr(value: string | null | undefined): PlanDropTarget | null {
  if (!value) return null;
  if (value === 'loose') return { type: 'loose' };
  const i = value.indexOf(':');
  if (i < 1) return null;
  const type = value.slice(0, i);
  const id = value.slice(i + 1);
  if (!id) return null;
  if (type === 'bundle' || type === 'feature') return { type, id };
  return null;
}

export function resolvePlanDrop(
  drag: PlanDragSource,
  target: PlanDropTarget | null,
  plan: DropPlan,
  labels: Record<string, string>,
  maxBundles: number
): PlanDropOutcome | null {
  if (!target) return null;
  const moving = plan.features.filter((f) => drag.featureIds.includes(f.id));
  if (moving.length === 0) return null;
  const members = moving.filter((f) => !!f.bundleId);
  const bundleName = (id: string | undefined) =>
    plan.bundles.find((b) => b.id === id)?.name ?? 'the bundle';

  // A loose row isn't a target for members. Only the strip under the list
  // takes a member out (Jason, Oct 14 2026: the gaps between rows read as
  // droppable before the cursor reached the strip), so a loose row says
  // where to go instead.
  if (target.type === 'feature' && members.length > 0) {
    return { kind: 'none', label: 'Drop it on the strip below to take it out' };
  }
  const effective: PlanDropTarget = target;

  if (effective.type === 'bundle') {
    const change = moving.filter((f) => f.bundleId !== effective.id);
    if (change.length === 0) {
      return { kind: 'none', label: `Already in ${bundleName(effective.id)}` };
    }
    const name = bundleName(effective.id);
    return change.some((f) => !!f.bundleId)
      ? { kind: 'move', bundleId: effective.id, label: `Move to ${name}` }
      : { kind: 'add', bundleId: effective.id, label: `Add to ${name}` };
  }

  if (effective.type === 'loose') {
    if (members.length === 0) return { kind: 'none', label: 'Not in a bundle' };
    const from = Array.from(new Set(members.map((f) => f.bundleId)));
    const label =
      from.length === 1 ? `Take out of ${bundleName(from[0])}` : 'Take out of their bundles';
    return { kind: 'remove', label };
  }

  // A loose row, and everything dragged is loose too.
  if (drag.featureIds.includes(effective.id)) {
    return { kind: 'none', label: 'Drop on another defense or a bundle' };
  }
  const withFeature = plan.features.find((f) => f.id === effective.id);
  if (!withFeature || withFeature.bundleId) return null;
  if (plan.bundles.length >= maxBundles) {
    return {
      kind: 'create',
      withFeatureId: effective.id,
      label: `This plan already has ${maxBundles} bundles`,
      disabled: true,
    };
  }
  return {
    kind: 'create',
    withFeatureId: effective.id,
    label: `Bundle with ${labels[effective.id] ?? 'this defense'}`,
  };
}

/** "bundle:<id>" or "feature:<id>" while a drop target should light up, ""
 *  otherwise. A string, so a component can select it and re-render only when
 *  the highlighted target changes. */
export function dropHighlightKey(
  drag: { target: PlanDropTarget | null; outcome: PlanDropOutcome | null } | null
): string {
  if (!drag?.target || !drag.outcome) return '';
  if (drag.outcome.kind === 'none' || drag.outcome.disabled) return '';
  // 'remove' lights nothing: the whole list is its target.
  if (drag.target.type === 'loose') return '';
  if (drag.outcome.kind === 'remove') return '';
  return planDropAttr(drag.target);
}
